// components/ReportUserModal.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import '../styles/Modals.scss';

const REPORT_REASONS = [
  { id: 'harassment', label: 'Harassment or Bullying' },
  { id: 'spam', label: 'Spam or Unwanted Contact' },
  { id: 'inappropriate_content', label: 'Inappropriate Content' },
  { id: 'fake_profile', label: 'Fake Profile' },
  { id: 'scam', label: 'Scam or Fraud' },
  { id: 'other', label: 'Other' },
];

const ReportUserModal = ({ isOpen, onClose, userId, userName }) => {
  const { user } = useAuth();
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [showBlockOption, setShowBlockOption] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) {
      alert('Please select a reason for reporting');
      return;
    }

    try {
      setIsSubmitting(true);

      // Submit report
      await supabase
        .from('user_reports')
        .insert({
          reporter_id: user.id,
          reported_user_id: userId,
          reason: selectedReason.id,
          description: description,
          status: 'pending',
          app_source: 'web',
          source_platform: 'web',
          created_at: new Date().toISOString(),
        });

      // Block user if requested
      if (showBlockOption) {
        await supabase
          .from('user_blocks')
          .insert({
            blocker_id: user.id,
            blocked_user_id: userId,
            created_at: new Date().toISOString(),
          });
      }

      alert(
        showBlockOption
          ? 'Thank you for reporting. The user has been blocked and our team will review this.'
          : 'Thank you for reporting. Our team will review this.'
      );
      handleClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      alert(error.message || 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setDescription('');
    setShowBlockOption(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <motion.div
            className="modal-content report-modal"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="modal-header">
              <h3>Report User</h3>
              <button className="close-btn" onClick={handleClose}>
                <FiX />
              </button>
            </div>

            {/* Content */}
            <div className="modal-body">
              {/* User Info */}
              <div className="user-info-box">
                <p>
                  You're reporting <strong>{userName || 'this user'}</strong>
                </p>
              </div>

              {/* Reason Selection */}
              <div className="form-section">
                <h4>Why are you reporting?</h4>
                <div className="reasons-list">
                  {REPORT_REASONS.map((reason) => (
                    <button
                      key={reason.id}
                      className={`reason-option ${
                        selectedReason?.id === reason.id ? 'selected' : ''
                      }`}
                      onClick={() => setSelectedReason(reason)}
                      type="button"
                    >
                      <div className="radio-btn">
                        {selectedReason?.id === reason.id && (
                          <div className="radio-inner"></div>
                        )}
                      </div>
                      <span>{reason.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="form-section">
                <h4>Additional Details (Optional)</h4>
                <textarea
                  className="textarea"
                  placeholder="Provide more context about why you're reporting..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  rows={4}
                />
                <div className="char-count">{description.length}/500</div>
              </div>

              {/* Block Option */}
              <button
                className="block-option"
                onClick={() => setShowBlockOption(!showBlockOption)}
                type="button"
              >
                <div className="checkbox">
                  {showBlockOption && <div className="check-mark">✓</div>}
                </div>
                <div className="block-option-text">
                  <h5>Block this user</h5>
                  <p>They won't be able to see your profile or message you</p>
                </div>
              </button>

              {/* Disclaimer */}
              <div className="disclaimer">
                <FiInfo />
                <p>Your report is anonymous. We'll review it within 24 hours.</p>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleSubmit}
                disabled={!selectedReason || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="spinner small"></div>
                ) : (
                  <>
                    <FiAlertTriangle />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ReportUserModal;